import type { ActorRefFrom, DoneActorEvent, ErrorActorEvent } from 'xstate'
import { assertEvent, fromPromise, setup } from 'xstate'
import type multiCartMachine from 'machines/cart/multiCartMachine'
import { deleteMultiCart } from 'api/cart/deleteMultiCart'
import { updateMultiCartName } from 'api/cart/updateMultiCartName'
import { softDeleteItemsFromMultiCart } from 'api/cart/softDeleteItemsFromMultiCart'
import { patchItemsInMultiCart } from 'api/cart/patchItemsInMultiCart'
import { reportClientError } from 'services/errorReportingService'
import { errorSnackbar, successSnackbar } from 'machines/cart/snackbarUtils'

type DeleteMultiCart = {
  type: 'delete multi cart'
  cartId: string
}

type UpdateMultiCart = {
  type: 'update multi cart'
  cartId: string
  name: string
}

type DeleteAllItemsFromActiveCart = {
  type: 'delete all items from active cart'
}

type UpdateSaveAllItemsForLater = {
  type: 'update save items for later'
  cartId: string
  itemIds: Array<string>
  saveForLater: boolean
  skipNotification?: boolean
}

type MultiCartReviewMachineEvents = UpdateMultiCart
  | DeleteMultiCart
  | DeleteAllItemsFromActiveCart
  | UpdateSaveAllItemsForLater
  | DoneActorEvent<App.MultiCartSummary, string>

type MultiCartReviewMachineContext = {
  multiCartMachineActorRef: ActorRefFrom<typeof multiCartMachine>
}

type MultiCartReviewMachineInput = {
  multiCartMachineActorRef: ActorRefFrom<typeof multiCartMachine>
}

export const multiCartReviewMachine = setup({
  types: {
    context: {} as MultiCartReviewMachineContext,
    events: {} as MultiCartReviewMachineEvents,
    input: {} as MultiCartReviewMachineInput,
  },
  actors: {
    deleteMultiCart: fromPromise(({ input }: { input: { cartId: string } }) => deleteMultiCart(input.cartId)),
    updateMultiCartName: fromPromise(({ input }: { input: { cartId: string, name: string } }) => updateMultiCartName(input.cartId, input.name)),
    softDeleteItemsFromMultiCart: fromPromise(({ input }: { input: { cartId: string, itemIdsToDelete: Array<string> } }) => softDeleteItemsFromMultiCart(input.cartId, input.itemIdsToDelete)),
    patchItemsInMultiCart: fromPromise(({ input }: { input: { cartId: string, itemIds: Array<string>, saveForLater: boolean, skipNotification: boolean } }) => patchItemsInMultiCart(input.cartId, { itemIds: input.itemIds, saveForLater: input.saveForLater, skipNotification: input.skipNotification })),
  },
  actions: {
    removeCartFromGlobalContext: ({ context }) => {
      context.multiCartMachineActorRef.send({ type: 'remove active cart id' })
    },
    assignUpdatedCartSummary: ({ context, event }) => {
      assertEvent(event, ['xstate.done.actor.0.multiCartReviewMachine.updatingMultiCart'])
      context.multiCartMachineActorRef.send({ type: 'update multi cart', cartId: event.output.cartId, name: event.output.name })
    },
    showUpdateCartSuccessSnackbar: () => {
      successSnackbar('Cart has been updated!')
    },
    showUpdateCartErrorSnackbar: () => {
      errorSnackbar('Failed to update cart')
    },
    showDeleteCartSuccessSnackbar: () => {
      successSnackbar('Cart has been deleted!')
    },
    showDeleteCartErrorSnackbar: () => {
      errorSnackbar('Failed to delete cart')
    },
    emptyCartItemsOfActiveCart: ({ context }) => {
      context.multiCartMachineActorRef.send({ type: 'empty cart items of active cart' })
    },
    showDeleteAllItemsFromCartErrorSnackbar: () => {
      errorSnackbar('Failed to delete all items from cart')
    },
    showSaveAllItemsForLaterErrorSnackbar: () => {
      errorSnackbar('Failed to update the cart')
    },
    logErrorEvent: ({ event }: { event: MultiCartReviewMachineEvents | ErrorActorEvent<Error, string> }, params?: { operation?: string }) => {
      // Only process ErrorActorEvent types
      if (!('error' in event)) return

      const operation = params?.operation || 'multiCartReview operation'
      reportClientError(event.error, {
        message: `MultiCartReview machine error: ${operation}`,
        actorId: event.actorId,
        stateMachineError: true,
      })
      console.error(`[MultiCartReview] ${operation} failed:`, event.error)
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFsCuAbALgSwMIEMAnTAJTADdswB3AWXwGMALbAOzADpCx8IBPAMQQw6MJjAACNFmwSGRTAG0ADAF1EoAA4B7WNhzbWGkAA9EAFnPKOARgAcAdgCsAGhB9EAJgCcAZg52Tn4OngBs9qG+jqEAvjFu0jgExGSUNPTMbJzcvIKomhD44lIYOHIKKupIIDp6BkbVZgiW1vbObh4ITk6hHA5t5g7hdpF2dnEJpXgKqVR0jCzsXDz8QiJikvjo6BL6YMiwEgBmhNrIEow45JLyxJXGtfrYhsZNLbaOru6INsrdHN47OZfCFhqNYvEQIlpikKHMMotsis8gUipJYPhrrtxAdjtpCBJ0GjCPdqo96q8LFYPu1vgg7DZvBwnA4GZ4whEouNIdDkqQ4ekFlllrk1qJintcSczhIoNhrqxync1A9dE8Xo1EKFrL5lOZQp8OohvMomf1PA4osplL5PE5zDYJlCpnzZoLMkscqsmEQIBJhOLJJLDtLznKFUqlCqyWqKZrmp4jQhWQ4OLrQuYxtbbfbHTyXTMBfMPUjRahWBBtP71uJSVpY88GqAmkNbD07UnQt4bGmnL5fJFs3aHU7eYW0sXERwMZRWFAAJI42AAMXxABliUJDJw2ORtABrThj2EThHCmdsBdL1eEDfiQgIXfaeT1Sp1moNjXNxBOO0cbXBF8nQONatj9I4HIjFyo4Fie8JCksF5zou+wruum5gIQpyEBwmhEpgRz4sgHDHvyp4IZwSFXqhN53phj6sHuL6Nm+0b1nUjaUs0-wON4Bq0p02qpp4AxDJyYwwTIrpFmeSwBmIl60LBmBbksT6HiRylupOwryTgc5KVJCgMUxRQsWo77kpx8bmN4TgcJ4vjAgJPwOOYHDmH2NjsmCURDJJSTjvBJYcHpinKQImHYbh+GEYQxGkdpsmcGFBnKSZz5mYYrFVOx6pNqYP42PZzlAYgDIed4VXeGJUHRAFMJkcFU75IU+lQIZgXEKpO6MQeR5aTJFEcK1ZlpUZxAZcx2UWWxH4cV+hXNCaaYmqEHZ0s5abebZtWjP5+YTU17otai7WdY1kVYfiMVFHFCWDeRIWjed6VPtNrA5aqC0FU0Ti-MyrLeWVCBOe5vhBCCkH7RCkxHUlw2pVAACC2woQcy6nMgyMMFcYB8j1HDqQN8NDSFSOo+g6MrljON43yU1ZZ9s25fN+VcU4yg9i5CB8f4No7Xt0GHV1x06XJNaXpT1OY2cdPyvjChXdFeF3URmmk09U4U2j1607jCsM+9TNfTGP1ce8bQg441jKEDPnidykKsNowjwNUiVk4i33s-GAC0oRJn79nVaHYc1d4DXSVrwpep0eVxt+CA2MCHlAiCIO-CCtj2ICdhDrmUdBSd56YpeMvofePuJ0tDj9P+hp0v07k7VVQsSSLjUI+TkvjaL1fWUndc9vxmdjB5yjrYMvn1Z30fNcKL3hUdA+LS29ej0mnk9mE7bQ35sPOprC8S+KUu6zR+v0woq+-Ygw8NzzfN9CyDJc47cRxEAA */
  id: 'multiCartReviewMachine',
  initial: 'ready',
  context: ({ input }: { input: MultiCartReviewMachineInput }) => ({
    multiCartMachineActorRef: input.multiCartMachineActorRef,
  }),
  states: {
    ready: {
      on: {
        'delete multi cart': 'deletingMultiCart',
        'update multi cart': 'updatingMultiCart',
        'delete all items from active cart': 'deletingAllItemsFromActiveCart',
        'update save items for later': 'savingItemsForLater',
      },
    },
    savingItemsForLater: {
      invoke: {
        src: 'patchItemsInMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'update save items for later')
          return { cartId: event.cartId, itemIds: event.itemIds, saveForLater: event.saveForLater, skipNotification: event.skipNotification ?? false }
        },
        onDone: [{
          target: 'ready',
          // send event to multiCartMachine to update the items
          actions: [({ event }) => {
            if (event.output.skipNotification) return
            const mulitpleItems = event.output.itemIds.length > 1

            if (event.output.saveForLater) {
              const message = mulitpleItems ? 'All items have been saved for later' : 'Item has been saved for later'
              successSnackbar(message)
              return
            }

            const message = mulitpleItems ? 'All items have been added back to the cart' : 'Item has been added back to the cart'
            successSnackbar(message)
          }, ({ context, event }) => {
            context.multiCartMachineActorRef.send({ type: 'update cart items save for later', itemIds: event.output.itemIds, saveForLater: event.output.saveForLater })
          }],
        }],
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'patchItemsInMultiCart' } },
            'showSaveAllItemsForLaterErrorSnackbar',
          ],
        },
      },
    },
    deletingMultiCart: {
      invoke: {
        src: 'deleteMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'delete multi cart')
          return { cartId: event.cartId }
        },
        onDone: {
          target: 'ready',
          actions: ['showDeleteCartSuccessSnackbar', 'removeCartFromGlobalContext'],
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'deleteMultiCart' } },
            'showDeleteCartErrorSnackbar',
          ],
        },
      },
    },
    updatingMultiCart: {
      invoke: {
        src: 'updateMultiCartName',
        input: ({ event }: { event: MultiCartReviewMachineEvents }) => {
          assertEvent(event, 'update multi cart')
          return {
            cartId: event.cartId,
            name: event.name,
          }
        },
        onDone: {
          target: 'ready',
          actions: ['showUpdateCartSuccessSnackbar', 'assignUpdatedCartSummary'],
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'updateMultiCartName' } },
            'showUpdateCartErrorSnackbar',
          ],
        },
      },
    },
    deletingAllItemsFromActiveCart: {
      invoke: {
        src: 'softDeleteItemsFromMultiCart',
        input: ({ context }) => {
          const activeCartId = context.multiCartMachineActorRef.getSnapshot().context.activeCartId
          const cartItems = context.multiCartMachineActorRef.getSnapshot().context.cartItems
          return {
            cartId: activeCartId!,
            itemIdsToDelete: cartItems.status === 'loaded' && activeCartId ? Object.keys(cartItems.data[activeCartId]) : [],
          }
        },
        onDone: {
          target: 'ready',
          actions: ['emptyCartItemsOfActiveCart'],
        },
        onError: {
          target: 'ready',
          actions: [
            { type: 'logErrorEvent', params: { operation: 'softDeleteItemsFromMultiCart' } },
            'showDeleteAllItemsFromCartErrorSnackbar',
          ],
        },
      },
    },
  },
})

export type MultiCartReviewActor = ActorRefFrom<typeof multiCartReviewMachine>
